import { Nullable } from '@utils/util.types';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useUser, useUsersStore } from 'services/users';
import { AuthenticationLoader } from '@components/shared/AuthenticationLoader';

export interface ProtectedRouteProps {
	children: React.ReactNode;
}

export interface DecodedToken {
	aud: string;
	email: string;
	exp: number;
	iat: number;
	iss: string;
	role: string;
	sub: string;
}
export function decodeToken(token: string): Nullable<DecodedToken> {
	if (!token) {
		return null;
	}

	try {
		return jwtDecode(token) || null;
	} catch (error) {
		console.error('Failed to decode token:', error);

		return null;
	}
}

export interface RoleGuardProps {
	children: React.ReactNode;
	allowedRole: 'farmer' | 'specialist' | 'company' | 'admin';
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
	const isLoggedIn = Cookies.get('accessToken');
	const { i18n } = useTranslation();

	useUser();

	return isLoggedIn ? <>{children}</> : <Navigate to={`/${i18n.language}/login`} />;
};

export const UnregisteredGuard = ({ children }: { children: ReactNode }) => {
	const { user, loading } = useUsersStore();
	const { i18n } = useTranslation();

	useUser();

	if (loading) {
		return <AuthenticationLoader />;
	}

	if (user?.user_completed_registration) {
		return <Navigate to={`/${i18n.language}/${user.role.toLowerCase()}/dashboard`} />;
	}

	return <>{children}</>;
};

export const RoleGuard = ({ children, allowedRole }: RoleGuardProps) => {
	const { i18n } = useTranslation();

	const accessToken = Cookies.get('accessToken');

	const decodedToken = decodeToken(accessToken || '');

	const role = decodedToken?.role.toLowerCase();

	if (role !== allowedRole) {
		if (role === 'admin') {
			return <Navigate to={`/${i18n.language}/admin`} />;
		}
		return <Navigate to={`/${i18n.language}/${role}/dashboard`} />;
	}

	return <>{children}</>;
};

export const AuthenticatedGuard = ({ children }: ProtectedRouteProps) => {
	const { i18n } = useTranslation();
	const accessToken = Cookies.get('accessToken');

	// Use these hooks unconditionally
	const { data, isLoading } = useUser();

	// Show authentication loader while checking user status
	if (isLoading) {
		return <AuthenticationLoader />;
	}

	const decodedToken = decodeToken(accessToken || '');
	const role = decodedToken?.role?.toLowerCase();

	if (data?.user_completed_registration && accessToken) {
		// Handle admin role redirection
		if (role === 'admin') {
			return <Navigate to={`/${i18n.language}/admin`} />;
		}
		return <Navigate to={`/${i18n.language}/${role}/dashboard`} />;
	}

	return <>{children}</>;
};
